import { apiRoot, server } from './ApiConfig'
import "./Menu.css"
import React, { useState, useEffect, Suspense } from "react"
import axios from "axios"
import { Link, Navigate, Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { useGlobal } from './GlobalContext'
import { useT } from './i18n/LanguageContext'
import { ReactComponent as ChangeTopicIcon } from './svg/changetopic.svg'
import { ReactComponent as NewDocIcon } from './svg/newdoc.svg'
import { ReactComponent as OpenIcon } from './svg/open.svg'
import { ReactComponent as PlayIcon } from './svg/play.svg'
import { ReactComponent as RecordIcon } from './svg/record.svg'
import { ReactComponent as SaveIcon } from './svg/save.svg'
import { ReactComponent as UserSwitchIcon } from './svg/selectuser.svg'
import { ReactComponent as SettingsIcon } from './svg/settings.svg'
import { ReactComponent as StopIcon } from './svg/stop.svg'
import { ReactComponent as TopicEditIcon } from './svg/topicedit.svg'
import { ReactComponent as ReplacementEditIcon } from './svg/replacementrules.svg'
import { ReactComponent as WavIcon } from './svg/wav.svg'
import { ReactComponent as LogoIcon } from './svg/logo.svg'
import { ReactComponent as WhatCanISayIcon } from './svg/whatcanisay.svg'
import { ReactComponent as ProfileIcon } from './svg/profile.svg'
import { ReactComponent as InfoIcon } from './svg/info.svg'
import ChangeTopic from './Topics/ChangeTopic'

const VdGrid = React.lazy(() => import('./VoxDocuments/VdGrid'));
const EditTopic = React.lazy(() => import('./Topics/EditTopic'));
const VoiceCommands = React.lazy(() => import('./Topics/VoiceCommands'));
const ReplacementEditor = React.lazy(() => import('./Topics/ReplacementEditor'));
const DictationLog = React.lazy(() => import('./Admin/DictationLog'));
const UserManagement = React.lazy(() => import('./Admin/UserManagement'));
const UserGroupManagement = React.lazy(() => import('./Admin/UserGroupManagement'));
const AdminFunctions = React.lazy(() => import('./Admin/AdminFunctions'));
const AllDocuments = React.lazy(() => import('./Admin/AllDocuments'));
const TranscriptionistInbox = React.lazy(() => import('./VoxDocuments/TranscriptionistInbox'));
const I18nView = React.lazy(() => import('./i18n/I18nView'));
const Settings = React.lazy(() => import('./Settings/Settings'));

const formatElapsed = (totalSeconds) => {
  let rest = Math.max(0, Math.floor(Number(totalSeconds) || 0))
  const days = Math.floor(rest / 86400)
  rest -= days * 86400
  const hours = Math.floor(rest / 3600)
  rest -= hours * 3600
  const minutes = Math.floor(rest / 60)
  const seconds = rest - minutes * 60
  const parts = []
  if (days > 0) parts.push(`${days}d`)
  if (days > 0 || hours > 0) parts.push(`${hours}h`)
  if (days > 0 || hours > 0 || minutes > 0) parts.push(`${minutes}m`)
  parts.push(`${seconds}s`)
  return parts.join(" ")
}

const Menu = ({ userEmail, sessionId, userAdmin, setSessionId, setUserEmail, isLoginMode = false }) => {
  const t = useT()
  const { adminWatch, setAdminWatch } = useGlobal()
  const { userRole, setUserRole, workflowRoles } = useGlobal()
  const isTranscriptionistWorkflow =
    (Array.isArray(workflowRoles) && workflowRoles.includes("transcriptionist")) ||
    userRole === "Corrector" ||
    userRole === "Transcriptionist"
  const showAssignedDocuments = isTranscriptionistWorkflow
  const showAuthorOpenBadges =
    (Array.isArray(workflowRoles) && workflowRoles.includes("author")) ||
    userRole === "Author"
  const [tsBadges, setTsBadges] = useState({ pending: 0, claimed: 0 })
  const [authorBadges, setAuthorBadges] = useState({ sent: 0, inTranscription: 0, completed: 0 })
  const { isRecording, setIsRecording } = useGlobal()
  const { isCheckingServerMessages, setIsCheckingServerMessages } = useGlobal()
  const { activeMenuItem, setActiveMenuItem, setActiveMenu, setWorkflowBadgeRevision } = useGlobal()
  const { isRecordingRef, selectedTopic, selectedDoc, dictateAction, setDictateAction, showEditor, setShowEditor, openDocumentAction, setOpenDocumentAction, newDocumentAction, setNewDocumentAction, replayRecognitionAction, setReplayRecognitionAction, audioDuration, isPlaying, isReplaying, saveDocumentAction, setSaveDocumentAction, documentDirty, setDocumentDirty, pendingNavigationRef, showEditor: globalShowEditor, setShowEditor: setGlobalShowEditor, documentReadOnly, setDocumentReadOnly, dictationBlocked } = useGlobal()

  const navigate = useNavigate()
  const location = useLocation()
  const [leavePrompt, setLeavePrompt] = useState(null)

  const requestNavigate = (action) => {
    if (!documentDirty) {
      action()
      return
    }
    setLeavePrompt({ action })
  }

  const handleLeaveSave = () => {
    if (!leavePrompt) return
    pendingNavigationRef.current = leavePrompt.action
    setSaveDocumentAction(true)
    setLeavePrompt(null)
  }

  const handleLeaveDiscard = () => {
    if (!leavePrompt) return
    const action = leavePrompt.action
    setDocumentDirty(false)
    setLeavePrompt(null)
    action()
  }

  const handleLeaveCancel = () => {
    setLeavePrompt(null)
  }

  const saveDisabled = isLoginMode || isRecordingRef || documentReadOnly || !documentDirty || !selectedDoc?.uid
  // Stop must stay available while recording; start is blocked on read-only / other author's workflow docs
  const dictateDisabled = isLoginMode || (!isRecordingRef && (documentReadOnly || dictationBlocked)) || !selectedTopic?.topic_id


  useEffect(() => {
    if (location.pathname === "/menu") {
      navigate("/menu/voxdocuments", { replace: true })
    }
  }, [location.pathname, navigate, userRole])

  // Browser refresh: restore menu/view from URL (Open Document ≠ Editor View)
  useEffect(() => {
    if (isLoginMode) return
    const path = location.pathname
    if (path.endsWith("/open-documents")) {
      setGlobalShowEditor(false)
      setActiveMenu("openDocument")
    } else if (path.endsWith("/assigned-documents")) {
      setGlobalShowEditor(false)
      setActiveMenu("assignedDocuments")
    } else if (path.endsWith("/voxdocuments")) {
      setGlobalShowEditor(true)
      setActiveMenu("editorView")
    } else if (path.endsWith("/change-topic")) {
      setActiveMenu("changeTopic")
    } else if (path.endsWith("/topic-editor")) {
      setActiveMenu("topicEditor")
    } else if (path.endsWith("/replacement-editor")) {
      setActiveMenu("replacementEditor")
    } else if (path.endsWith("/voice-commands")) {
      setActiveMenu("voiceCommands")
    } else if (path.endsWith("/settings")) {
      setActiveMenu("settings")
    } else if (path.endsWith("/all-documents")) {
      setActiveMenu("allDocuments")
    } else if (path.endsWith("/dictation-log")) {
      setActiveMenu("dictationLog")
    } else if (path.endsWith("/user-management")) {
      setActiveMenu("userManagement")
    } else if (path.endsWith("/admin-functions")) {
      setActiveMenu("adminFunctions")
    } else if (path.endsWith("/i18n")) {
      setActiveMenu("i18n")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isLoginMode])

  const [serverMessages, setServerMessages] = useState([])
  const [pulseLive, setPulseLive] = useState(false)
  const [pulseLiveText, setPulseLiveText] = useState("")
  const [pulseElapsed, setPulseElapsed] = useState(null)
  const [pulseReceivedAt, setPulseReceivedAt] = useState(0)
  const [pulseNow, setPulseNow] = useState(0)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [okButtonEnabled, setOkButtonEnabled] = useState(false)

  useEffect(() => {
    if (secondsLeft > 0) {
      const timer = setTimeout(() => setSecondsLeft(secondsLeft - 1), 1000)
      setOkButtonEnabled(false)
      return () => clearTimeout(timer)
    } else {
      setOkButtonEnabled(true)
    }
  }, [secondsLeft])

  async function fetchServerMessages() {
    try {
      const response = await axios.post(apiRoot + "api/info/getServerMessages", {
        sessionId: sessionId,
        topicId: 1,
        email: userEmail,
        buildNumber: 1,
        version: 1,
        platform: "portal",
      })
      if (response.data.success) {
        if (response.data.results.length > 0) {
          console.log("VAN SERVER MESSAGE:", response.data.results)
          setSecondsLeft(10)
          setServerMessages(response.data.results)
        }
      } else if (response.data.error === "Expired session!") {
        localStorage.removeItem("sessionId")
        navigate("/login")
      }
    } catch (error) {
      console.log("getServerMessages error!", error)
    }
  }

  useEffect(() => {
    if (isLoginMode) return
    const intervalId = setInterval(() => {
      if (!isRecording) {
        fetchServerMessages()
      }
    }, 60000)
    return () => clearInterval(intervalId)
  }, [isRecording, isLoginMode])

  useEffect(() => {
    if (isLoginMode || !sessionId) return
    if (!showAssignedDocuments && !showAuthorOpenBadges) {
      setTsBadges({ pending: 0, claimed: 0 })
      setAuthorBadges({ sent: 0, inTranscription: 0, completed: 0 })
      return
    }

    let cancelled = false
    let timerId = null
    let revisionRef = ""

    const scheduleNext = () => {
      if (cancelled) return
      timerId = setTimeout(tick, 5000)
    }

    const applyCounts = (r) => {
      const nextTs = {
        pending: Number(r.transcriptionist?.pending) || 0,
        claimed: Number(r.transcriptionist?.claimed) || 0,
      }
      const nextAuthor = {
        sent: Number(r.author?.sent) || 0,
        inTranscription: Number(r.author?.inTranscription) || 0,
        completed: Number(r.author?.completed) || 0,
      }
      setTsBadges((prev) =>
        prev.pending === nextTs.pending && prev.claimed === nextTs.claimed ? prev : nextTs
      )
      setAuthorBadges((prev) =>
        prev.sent === nextAuthor.sent &&
        prev.inTranscription === nextAuthor.inTranscription &&
        prev.completed === nextAuthor.completed
          ? prev
          : nextAuthor
      )
    }

    const loadBadges = async () => {
      // Skip while hidden or dictating — cuts useless load
      if (document.visibilityState === "hidden" || isRecording) return
      try {
        const response = await axios.post(apiRoot + "api/voxdocument/getWorkflowBadgeCounts", {
          sessionId,
          revision: revisionRef || undefined,
        })
        if (cancelled) return
        if (response.data.success === false) {
          if (response.data.error === "Expired session!") {
            localStorage.removeItem("sessionId")
            navigate("/login")
          }
          return
        }
        if (response.data.revision) revisionRef = response.data.revision
        if (response.data.unchanged) return
        applyCounts(response.data.results || {})
        if (response.data.revision) setWorkflowBadgeRevision(response.data.revision)
      } catch (error) {
        if (!cancelled) console.error("getWorkflowBadgeCounts error", error)
      }
    }

    const tick = async () => {
      await loadBadges()
      scheduleNext()
    }

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Immediate refresh when user returns to the tab
        if (timerId) clearTimeout(timerId)
        tick()
      }
    }

    document.addEventListener("visibilitychange", onVisibility)
    tick()

    return () => {
      cancelled = true
      if (timerId) clearTimeout(timerId)
      document.removeEventListener("visibilitychange", onVisibility)
    }
  }, [isLoginMode, sessionId, showAssignedDocuments, showAuthorOpenBadges, navigate, isRecording, setWorkflowBadgeRevision])

  useEffect(() => {
    if (isLoginMode || userRole !== "Admin" || !sessionId) {
      setPulseLive(false)
      setPulseLiveText("")
      setPulseElapsed(null)
      return
    }
    let cancelled = false
    const loadPulse = async () => {
      try {
        const response = await axios.post(apiRoot + "api/log/getDictationActivity", { sessionId })
        if (cancelled) return
        if (response.data.success === false) {
          if (response.data.error === "Expired session!") {
            localStorage.removeItem("sessionId")
            navigate("/login")
          }
          setPulseLive(false)
          setPulseLiveText("")
          setPulseElapsed(null)
          return
        }
        const results = response.data.results || {}
        const live = Number(results.liveCount) || 0
        if (live > 0) {
          setPulseLive(true)
          setPulseLiveText(live > 1 ? `live:${live}` : "live")
          setPulseElapsed(null)
          return
        }
        setPulseLive(false)
        setPulseLiveText("")
        if (results.elapsedSeconds == null || Number.isNaN(Number(results.elapsedSeconds))) {
          setPulseElapsed(null)
          setPulseLiveText("none")
          return
        }
        const receivedAt = Date.now()
        setPulseElapsed(Number(results.elapsedSeconds))
        setPulseReceivedAt(receivedAt)
        setPulseNow(receivedAt)
      } catch (error) {
        if (!cancelled) console.error("getDictationActivity error", error)
      }
    }
    loadPulse()
    const intervalId = setInterval(loadPulse, 5000)
    return () => {
      cancelled = true
      clearInterval(intervalId)
    }
  }, [isLoginMode, userRole, sessionId, navigate])

  useEffect(() => {
    if (pulseLive || pulseElapsed == null) return
    const intervalId = setInterval(() => setPulseNow(Date.now()), 1000)
    return () => clearInterval(intervalId)
  }, [pulseLive, pulseElapsed])

  const pulseLiveLabel = pulseLiveText === "live"
    ? t("userui.menu.dictatingNow", "Dictating now")
    : pulseLiveText.startsWith("live:")
      ? t("userui.menu.dictatingNowCount", { count: pulseLiveText.slice(5) }, "Dictating now · {{count}}")
      : pulseLiveText === "none"
        ? t("userui.menu.noDictationYet", "No dictation yet")
        : pulseLiveText
  const pulseText = pulseLive
    ? pulseLiveLabel
    : pulseElapsed == null
      ? pulseLiveLabel
      : t("userui.menu.lastDictation", { elapsed: formatElapsed(pulseElapsed + Math.max(0, pulseNow - pulseReceivedAt) / 1000) }, "Last dictation {{elapsed}}")

  const loadUserProfile = () => { }

   /*
  ██   ██ ████████ ███    ███ ██      
  ██   ██    ██    ████  ████ ██      
  ███████    ██    ██ ████ ██ ██      
  ██   ██    ██    ██  ██  ██ ██      
  ██   ██    ██    ██      ██ ███████ 
  */

  return (
    <div name="mainContainer" className={`vd-wapp ${isLoginMode ? 'vd-wapp--login' : ''}`}>
      <div className="vd-wbody">
        {/* Left Sidebar */}
          <div className={`vd-wside ${adminWatch ? "vd-wside--admin" : ""}`}>
            <div className="vd-wside__fixed">
            {/* Logo section */}
            <div style={{ marginBottom: '10px', paddingBottom: '16px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: '11px' }}>
              <LogoIcon style={{ height: '21px', width: 'auto', flex: 'none' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', lineHeight: 1 }}>
                <span style={{ width: 1, height: 16, background: 'var(--line)' }} />
                <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)', letterSpacing: '.2px' }}>
                  VoxDoc
                </span>
              </div>
            </div>

            {userRole === "Admin" && (
              <p className={"vd-wside__pulse" + (pulseLive ? " vd-wside__pulse--live" : "")}>
                {pulseText || "…"}
              </p>
            )}
            
            <p style={{ fontSize: '14px', color: 'var(--muted)', marginTop: '12px' }}>{server}</p>
            <p style={{ fontSize: '12px', color: 'var(--blue-700)', marginTop: '2px' }}>{userEmail}</p>

            {/* VoxDocument Navigation */}
            {!isTranscriptionistWorkflow && <div className="vd-wgrp">
              <button
                className={`recpill ${isRecordingRef ? 'recpill--on' : ''}`}
                onClick={() => {
                  if (dictateDisabled) return
                  navigate("/menu/voxdocuments")
                  setGlobalShowEditor(true)
                  setActiveMenu("editorView")
                  if (isRecordingRef) {
                    setDictateAction('stop')
                  } else if (selectedTopic?.topic_id) {
                    setDictateAction('start')
                  }
                }}
                disabled={dictateDisabled}
                title={!isRecordingRef && (documentReadOnly || dictationBlocked) ? t("userui.menu.dictationNotAllowed", "Dictation is not allowed on this document") : undefined}
                style={{ cursor: dictateDisabled ? 'not-allowed' : 'pointer', opacity: dictateDisabled ? 0.5 : 1, width: '100%' }}
              >
                <span className="recpill__icon">
                  {isRecordingRef ? (
                    <span className="recpill__rec"></span>
                  ) : (
                    <span className="recpill__ring"></span>
                  )}
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginLeft: '8px' }}>
                  <span>{isRecordingRef ? t("menu.recording", "Recording") : t("menu.dictate", "Dictate")}</span>
                  {selectedTopic?.name && (
                    <span style={{ fontSize: '10px', opacity: 0.8, marginTop: '0px', fontWeight: 400 }}>
                      {t("userui.menu.topicModel", { name: selectedTopic.name }, "{{name}} model")}
                    </span>
                  )}
                </div>
              </button>
            </div>}
            </div>

            <div className="vd-wside__scroll">
            {/* Recording Navigation */}
            {!isTranscriptionistWorkflow && <div className="vd-wgrp">
              <div className="vd-wgrp__label">{t("menu.group.recording")}</div>
              <button className={`vd-wnav ${activeMenuItem === "editorView" ? "vd-wnav--on" : ""}`} onClick={() => { navigate("/menu/voxdocuments"); setGlobalShowEditor(true); setActiveMenu("editorView"); }} disabled={isLoginMode || isRecordingRef} style={{ width: '223px', height: '36px', cursor: (isLoginMode || isRecordingRef) ? 'not-allowed' : 'pointer', opacity: (isLoginMode || isRecordingRef) ? 0.5 : 1 }}>
                <div className="vd-wnav__ico">
                  <RecordIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                </div>
                <span className="vd-wnav__txt">{t("menu.showEditor")}</span>
                {activeMenuItem === "editorView" && <div className="vd-wnav__bar"></div>}
              </button>
              <button className="vd-wnav" disabled={true} style={{ width: '223px', height: '36px', cursor: 'not-allowed', opacity: 0.5 }}>
                <div className="vd-wnav__ico">
                  <WavIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                </div>
                <span className="vd-wnav__txt">{t("menu.recognizeAudioFile")}</span>
              </button>

              <button
                className={`vd-wnav${isReplaying ? " vd-wnav--on" : ""}`}
                onClick={() => {
                  // Ensure editor route is active so VdGrid can handle replay start/stop
                  navigate("/menu/voxdocuments")
                  setGlobalShowEditor(true)
                  setActiveMenu("editorView")
                  setReplayRecognitionAction(true)
                }}
                disabled={isLoginMode || audioDuration === 0 || isRecordingRef || (!showEditor && !selectedDoc && !isReplaying)}
                style={{ width: '223px', height: '36px', cursor: (isLoginMode || audioDuration === 0 || isRecordingRef || (!showEditor && !selectedDoc && !isReplaying)) ? 'not-allowed' : 'pointer', opacity: (isLoginMode || audioDuration === 0 || isRecordingRef || (!showEditor && !selectedDoc && !isReplaying)) ? 0.5 : 1 }}
              >
                <div className="vd-wnav__ico">
                  {isReplaying ? <StopIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} /> : <PlayIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />}
                </div>
                <span className="vd-wnav__txt">{isReplaying ? t("menu.stopReplay") : t("menu.playback")}</span>
                {isReplaying && <div className="vd-wnav__bar"></div>}
              </button>
            </div>}

            {/* Document Navigation */}
            <div className="vd-wgrp">
              <div className="vd-wgrp__label">{t("menu.group.document")}</div>
              {!isTranscriptionistWorkflow && <button className={`vd-wnav ${activeMenuItem === "newDocument" ? "vd-wnav--on" : ""}`} onClick={() => requestNavigate(() => { setDocumentReadOnly(false); setAdminWatch(false); navigate("/menu/voxdocuments"); setNewDocumentAction(true); setGlobalShowEditor(true); setActiveMenu("editorView"); })} disabled={isLoginMode || isRecordingRef} style={{ width: '223px', height: '36px', cursor: (isLoginMode || isRecordingRef) ? 'not-allowed' : 'pointer', opacity: (isLoginMode || isRecordingRef) ? 0.5 : 1 }}>
                <div className="vd-wnav__ico">
                  <NewDocIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                </div>
                <span className="vd-wnav__txt">{t("menu.newDocument")}</span>
                {activeMenuItem === "newDocument" && <div className="vd-wnav__bar"></div>}
              </button>}
              {!isTranscriptionistWorkflow && <button className={`vd-wnav ${activeMenuItem === "openDocument" || location.pathname.endsWith("/open-documents") ? "vd-wnav--on" : ""}`} onClick={() => requestNavigate(() => { setDocumentReadOnly(false); setAdminWatch(false); navigate("/menu/open-documents"); setOpenDocumentAction(true); setGlobalShowEditor(false); setActiveMenu("openDocument"); })} disabled={isLoginMode || isRecordingRef} style={{ width: '223px', height: '36px', cursor: (isLoginMode || isRecordingRef) ? 'not-allowed' : 'pointer', opacity: (isLoginMode || isRecordingRef) ? 0.5 : 1 }}>
                <div className="vd-wnav__ico">
                  <OpenIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                </div>
                <span className="vd-wnav__txt">{t("menu.openDocument")}</span>
                {showAuthorOpenBadges &&
                  (authorBadges.sent > 0 || authorBadges.inTranscription > 0 || authorBadges.completed > 0) && (
                  <span className="vd-wnav__badges" aria-label={t("userui.menu.openBadgesAria", "Open Document badges")}>
                    {authorBadges.sent > 0 && (
                      <span className="vd-wnav__badge vd-wnav__badge--pending" title={t("userui.menu.badgeSent", "Sent for transcription")}>
                        {authorBadges.sent}
                      </span>
                    )}
                    {authorBadges.inTranscription > 0 && (
                      <span className="vd-wnav__badge vd-wnav__badge--claimed" title={t("userui.menu.badgeInTranscription", "In transcription")}>
                        {authorBadges.inTranscription}
                      </span>
                    )}
                    {authorBadges.completed > 0 && (
                      <span className="vd-wnav__badge vd-wnav__badge--completed" title={t("userui.menu.badgeComplete", "Transcription complete")}>
                        {authorBadges.completed}
                      </span>
                    )}
                  </span>
                )}
                {(activeMenuItem === "openDocument" || location.pathname.endsWith("/open-documents")) && <div className="vd-wnav__bar"></div>}
              </button>}
              {showAssignedDocuments && (
                <Link
                  to="/menu/assigned-documents"
                  className={`vd-wnav ${location.pathname.endsWith("/assigned-documents") ? "vd-wnav--on" : ""}`}
                  onClick={(e) => {
                    e.preventDefault()
                    if (isLoginMode || isRecordingRef) return
                    requestNavigate(() => {
                      navigate("/menu/assigned-documents")
                      setActiveMenu("assignedDocuments")
                      setGlobalShowEditor(false)
                    })
                  }}
                  style={{ width: '223px', height: '36px', pointerEvents: (isLoginMode || isRecordingRef) ? 'none' : 'auto', opacity: (isLoginMode || isRecordingRef) ? 0.5 : 1 }}
                >
                  <div className="vd-wnav__ico">
                    <OpenIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                  </div>
                  <span className="vd-wnav__txt">{isTranscriptionistWorkflow ? t("menu.openDocument") : t("menu.assignedDocuments")}</span>
                  {(tsBadges.pending > 0 || tsBadges.claimed > 0) && (
                    <span className="vd-wnav__badges" aria-label={t("userui.menu.assignedBadgesAria", "Assigned Documents badges")}>
                      {tsBadges.pending > 0 && (
                        <span className="vd-wnav__badge vd-wnav__badge--pending" title={t("userui.menu.badgeAwaitingClaim", "Sent for transcription (awaiting claim)")}>
                          {tsBadges.pending}
                        </span>
                      )}
                      {tsBadges.claimed > 0 && (
                        <span className="vd-wnav__badge vd-wnav__badge--claimed" title={t("userui.menu.badgeClaimed", "Claimed by you")}>
                          {tsBadges.claimed}
                        </span>
                      )}
                    </span>
                  )}
                  {location.pathname.endsWith("/assigned-documents") && <div className="vd-wnav__bar"></div>}
                </Link>
              )}
              <button className={`vd-wnav ${activeMenuItem === "saveDocument" ? "vd-wnav--on" : ""}`} onClick={() => { if (documentReadOnly) return; setSaveDocumentAction(true); setGlobalShowEditor(true); setActiveMenu("editorView"); }} disabled={saveDisabled} style={{ width: '223px', height: '36px', cursor: saveDisabled ? 'not-allowed' : 'pointer', opacity: saveDisabled ? 0.5 : 1 }}>
                <div className="vd-wnav__ico">
                  <SaveIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                </div>
                <span className="vd-wnav__txt">{t("menu.saveDocument")}</span>
                {activeMenuItem === "saveDocument" && <div className="vd-wnav__bar"></div>}
              </button>
            </div>

            {/* Switch Navigation */}
            <div className="vd-wgrp">
              <div className="vd-wgrp__label">{t("menu.group.switch")}</div>
              <button className="vd-wnav" onClick={() => requestNavigate(() => {
                localStorage.removeItem("sessionId")
                navigate("/login")
              })} disabled={isLoginMode || isRecordingRef} style={{ width: '223px', height: '36px', cursor: (isLoginMode || isRecordingRef) ? 'not-allowed' : 'pointer', opacity: (isLoginMode || isRecordingRef) ? 0.5 : 1 }}>
                <div className="vd-wnav__ico">
                  <UserSwitchIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                </div>
                <span className="vd-wnav__txt">{t("menu.changeUser")}</span>
              </button>

              {!isTranscriptionistWorkflow && <Link to="/menu/change-topic" className={`vd-wnav ${location.pathname.endsWith("/change-topic") ? "vd-wnav--on" : ""}`} onClick={(e) => {
                if (isLoginMode || isRecordingRef || (selectedDoc && selectedDoc.document_id !== -1)) {
                  e.preventDefault()
                  return
                }
                e.preventDefault()
                requestNavigate(() => { navigate("/menu/change-topic"); setActiveMenu("changeTopic"); setGlobalShowEditor(false); })
              }} style={{ width: '223px', height: '36px', pointerEvents: (isLoginMode || isRecordingRef || (selectedDoc && selectedDoc.document_id !== -1)) ? 'none' : 'auto', opacity: (isLoginMode || isRecordingRef || (selectedDoc && selectedDoc.document_id !== -1)) ? 0.5 : 1 }}>
                <div className="vd-wnav__ico">
                  <ChangeTopicIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                </div>
                <span className="vd-wnav__txt">{t("menu.changeTopic")}</span>
                {location.pathname.endsWith("/change-topic") && <div className="vd-wnav__bar"></div>}
              </Link>}
            </div>

            {/* Context Navigation */}
            {!isTranscriptionistWorkflow && <div className="vd-wgrp">
              <div className="vd-wgrp__label">{t("menu.group.context")}</div>

              <Link to="/menu/topic-editor" className={`vd-wnav ${location.pathname.endsWith("/topic-editor") ? "vd-wnav--on" : ""}`} onClick={(e) => {
                e.preventDefault()
                if (isLoginMode || isRecordingRef) return
                requestNavigate(() => { navigate("/menu/topic-editor"); setActiveMenu("topicEditor"); setGlobalShowEditor(false); })
              }} style={{ width: '223px', height: '36px', pointerEvents: (isLoginMode || isRecordingRef) ? 'none' : 'auto', opacity: (isLoginMode || isRecordingRef) ? 0.5 : 1 }}>
                <div className="vd-wnav__ico">
                  <TopicEditIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                </div>
                <span className="vd-wnav__txt">{t("menu.topicEditor")}</span>
                {location.pathname.endsWith("/topic-editor") && <div className="vd-wnav__bar"></div>}
              </Link>

              <Link to="/menu/replacement-editor" className={`vd-wnav ${location.pathname.endsWith("/replacement-editor") ? "vd-wnav--on" : ""}`} onClick={(e) => {
                e.preventDefault()
                if (isLoginMode || isRecordingRef) return
                requestNavigate(() => { navigate("/menu/replacement-editor"); setActiveMenu("replacementEditor"); setGlobalShowEditor(false); })
              }} style={{ width: '223px', height: '36px', pointerEvents: (isLoginMode || isRecordingRef) ? 'none' : 'auto', opacity: (isLoginMode || isRecordingRef) ? 0.5 : 1 }}>
                <div className="vd-wnav__ico">
                  <ReplacementEditIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                </div>
                <span className="vd-wnav__txt">{t("menu.replacementEditor")}</span>
                {location.pathname.endsWith("/replacement-editor") && <div className="vd-wnav__bar"></div>}
              </Link>
              <button className={`vd-wnav ${activeMenuItem === "voiceCommands" ? "vd-wnav--on" : ""}`} onClick={() => requestNavigate(() => { navigate("/menu/voice-commands"); setGlobalShowEditor(false); setActiveMenu("voiceCommands"); })} disabled={isLoginMode || isRecordingRef} style={{ width: '223px', height: '36px', cursor: (isLoginMode || isRecordingRef) ? 'not-allowed' : 'pointer', opacity: (isLoginMode || isRecordingRef) ? 0.5 : 1 }}>
                <div className="vd-wnav__ico">
                  <WhatCanISayIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                </div>
                <span className="vd-wnav__txt">{t("menu.voiceCommands")}</span>
                {activeMenuItem === "voiceCommands" && <div className="vd-wnav__bar"></div>}
              </button>
            </div>}

            {/* Settings Navigation */}
            <div className="vd-wgrp">
              <div className="vd-wgrp__label">{t("menu.group.settings")}</div>
              <Link to="/menu/settings" className={`vd-wnav ${location.pathname.endsWith("/settings") ? "vd-wnav--on" : ""}`} onClick={(e) => {
                e.preventDefault()
                if (isLoginMode || isRecordingRef) return
                requestNavigate(() => { navigate("/menu/settings"); setActiveMenu("settings"); setGlobalShowEditor(false); })
              }} style={{ width: '223px', height: '36px', pointerEvents: (isLoginMode || isRecordingRef) ? 'none' : 'auto', opacity: (isLoginMode || isRecordingRef) ? 0.5 : 1 }}>
                <div className="vd-wnav__ico">
                  <SettingsIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                </div>
                <span className="vd-wnav__txt">{t("menu.settings")}</span>
                {location.pathname.endsWith("/settings") && <div className="vd-wnav__bar"></div>}
              </Link>
            </div>

            {/* Admin Navigation */}
            {(userRole === "Admin" || userRole === "Partner") && (
              <div className="vd-wgrp">
                <div className="vd-wgrp__label">{t("menu.group.admin")}</div>
                {userRole === "Admin" && (
                  <Link to="/menu/all-documents" className={`vd-wnav ${location.pathname.endsWith("/all-documents") ? "vd-wnav--on" : ""}`} onClick={(e) => {
                    e.preventDefault()
                    if (isLoginMode || isRecordingRef) return
                    requestNavigate(() => { navigate("/menu/all-documents"); setActiveMenu("allDocuments"); setGlobalShowEditor(false); })
                  }} style={{ width: '223px', height: '36px', pointerEvents: (isLoginMode || isRecordingRef) ? 'none' : 'auto', opacity: (isLoginMode || isRecordingRef) ? 0.5 : 1 }}>
                    <div className="vd-wnav__ico">
                      <OpenIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                    </div>
                    <span className="vd-wnav__txt">{t("menu.allDocuments")}</span>
                    {location.pathname.endsWith("/all-documents") && <div className="vd-wnav__bar"></div>}
                  </Link>
                )}

                {userRole === "Admin" && (
                  <Link to="/menu/dictation-log" className={`vd-wnav ${location.pathname.endsWith("/dictation-log") ? "vd-wnav--on" : ""}`} onClick={(e) => {
                    e.preventDefault()
                    if (isLoginMode || isRecordingRef) return
                    requestNavigate(() => { navigate("/menu/dictation-log"); setActiveMenu("dictationLog"); setGlobalShowEditor(false); })
                  }} style={{ width: '223px', height: '36px', pointerEvents: (isLoginMode || isRecordingRef) ? 'none' : 'auto', opacity: (isLoginMode || isRecordingRef) ? 0.5 : 1 }}>
                    <div className="vd-wnav__ico">
                      <InfoIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                    </div>
                    <span className="vd-wnav__txt">{t("menu.dictationLog")}</span>
                    {location.pathname.endsWith("/dictation-log") && <div className="vd-wnav__bar"></div>}
                  </Link>
                )}

                <Link to="/menu/user-management" className={`vd-wnav ${location.pathname.endsWith("/user-management") ? "vd-wnav--on" : ""}`} onClick={(e) => {
                  e.preventDefault()
                  if (isLoginMode || isRecordingRef) return
                  requestNavigate(() => { navigate("/menu/user-management"); setActiveMenu("userManagement"); setGlobalShowEditor(false); })
                }} style={{ width: '223px', height: '36px', pointerEvents: (isLoginMode || isRecordingRef) ? 'none' : 'auto', opacity: (isLoginMode || isRecordingRef) ? 0.5 : 1 }}>
                  <div className="vd-wnav__ico">
                    <ProfileIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                  </div>
                  <span className="vd-wnav__txt">{t("menu.userManagement")}</span>
                  {location.pathname.endsWith("/user-management") && <div className="vd-wnav__bar"></div>}
                </Link>

                {(userRole === "Admin" || userRole === "Partner") && (
                  <Link to="/menu/user-groups" className={`vd-wnav ${location.pathname.endsWith("/user-groups") || location.pathname.endsWith("/site-management") ? "vd-wnav--on" : ""}`} onClick={(e) => {
                    e.preventDefault()
                    if (isLoginMode || isRecordingRef) return
                    requestNavigate(() => { navigate("/menu/user-groups"); setActiveMenu("userGroups"); setGlobalShowEditor(false); })
                  }} style={{ width: '223px', height: '36px', pointerEvents: (isLoginMode || isRecordingRef) ? 'none' : 'auto', opacity: (isLoginMode || isRecordingRef) ? 0.5 : 1 }}>
                    <div className="vd-wnav__ico">
                      <ProfileIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                    </div>
                    <span className="vd-wnav__txt">{t("menu.userGroups")}</span>
                    {(location.pathname.endsWith("/user-groups") || location.pathname.endsWith("/site-management")) && <div className="vd-wnav__bar"></div>}
                  </Link>
                )}

                {userRole === "Admin" && (
                  <Link to="/menu/admin-functions" className={`vd-wnav ${location.pathname.endsWith("/admin-functions") ? "vd-wnav--on" : ""}`} onClick={(e) => {
                    e.preventDefault()
                    if (isLoginMode || isRecordingRef) return
                    requestNavigate(() => { navigate("/menu/admin-functions"); setActiveMenu("adminFunctions"); setGlobalShowEditor(false); })
                  }} style={{ width: '223px', height: '36px', pointerEvents: (isLoginMode || isRecordingRef) ? 'none' : 'auto', opacity: (isLoginMode || isRecordingRef) ? 0.5 : 1 }}>
                    <div className="vd-wnav__ico">
                      <InfoIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                    </div>
                    <span className="vd-wnav__txt">{t("menu.adminFunctions")}</span>
                    {location.pathname.endsWith("/admin-functions") && <div className="vd-wnav__bar"></div>}
                  </Link>
                )}

                {userRole === "Admin" && (
                  <Link to="/menu/i18n" className={`vd-wnav ${location.pathname.endsWith("/i18n") ? "vd-wnav--on" : ""}`} onClick={(e) => {
                    e.preventDefault()
                    if (isLoginMode || isRecordingRef) return
                    requestNavigate(() => { navigate("/menu/i18n"); setActiveMenu("i18n"); setGlobalShowEditor(false); })
                  }} style={{ width: '223px', height: '36px', pointerEvents: (isLoginMode || isRecordingRef) ? 'none' : 'auto', opacity: (isLoginMode || isRecordingRef) ? 0.5 : 1 }}>
                    <div className="vd-wnav__ico">
                      <InfoIcon width="20" height="20" style={{ display: "block", flex: "0 0 auto" }} />
                    </div>
                    <span className="vd-wnav__txt">i18n</span>
                    {location.pathname.endsWith("/i18n") && <div className="vd-wnav__bar"></div>}
                  </Link>
                )}
              </div>
            )}

            {/* User Section — profile card aligned with vd chrome */}
            <div className="vd-wgrp vd-wgrp--user">
              <div
                className="vd-uprofile vd-uprofile--identity"
              >
                <span className="vd-uprofile__av" aria-hidden>
                  {(userEmail || "?").trim().charAt(0).toUpperCase()}
                </span>
                <span className="vd-uprofile__meta">
                  <span className="vd-uprofile__name">{userEmail || t("menu.profile", "Profile")}</span>
                  <span className="vd-uprofile__role">{userRole || t("common.labels.user", "User")}</span>
                </span>
              </div>
            </div>
            </div>
          </div>

        {/* Main Content Area */}
        {!isLoginMode && (
          <div className="vd-wmain">
            <Suspense fallback={<div>{t("userui.menu.loading", "Loading...")}</div>}>
              <Routes>
                <Route path="/voxdocuments" element={<VdGrid sessionId={sessionId} />} />
                <Route path="/open-documents" element={<VdGrid sessionId={sessionId} listOnly />} />
                <Route path="/change-topic" element={<ChangeTopic sessionId={sessionId} />} />
                <Route path="/topic-editor" element={<EditTopic sessionId={sessionId} />} />
                <Route path="/replacement-editor" element={<ReplacementEditor sessionId={sessionId} topic={selectedTopic} />} />
                <Route path="/voice-commands" element={<VoiceCommands sessionId={sessionId} />} />
                <Route path="/user-profile" element={<Navigate to="/menu/settings?section=basicData" replace />} />
                <Route path="/dictation-log" element={<DictationLog sessionId={sessionId} />} />
                <Route path="/user-management" element={<UserManagement sessionId={sessionId} userRole={userRole} setSessionId={setSessionId} setUserEmail={setUserEmail} setUserRole={setUserRole} />} />
                <Route path="/user-groups" element={<UserGroupManagement sessionId={sessionId} userRole={userRole} />} />
                <Route path="/site-management" element={<UserGroupManagement sessionId={sessionId} userRole={userRole} />} />
                <Route path="/admin-functions" element={<AdminFunctions sessionId={sessionId} userRole={userRole} setSessionId={setSessionId} setUserEmail={setUserEmail} setUserRole={setUserRole} />} />
                <Route path="/all-documents" element={<AllDocuments sessionId={sessionId} />} />
                <Route path="/assigned-documents" element={<TranscriptionistInbox sessionId={sessionId} />} />
                <Route path="/i18n" element={<I18nView />} />
                <Route path="/settings" element={<Settings sessionId={sessionId} userEmail={userEmail} server={server} selectedTopic={selectedTopic} />} />
                <Route path="*" element={<div style={{ padding: '20px' }}>{t("userui.menu.selectFunction", "Select a function from Menu!")}</div>} />
              </Routes>
            </Suspense>
          </div>
        )}
      </div>

      {/* Server Messages Modal */}
      {serverMessages.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full text-black">
            <h2 className="text-xl font-bold mb-4">{t("userui.menu.serverMessages", "Server messages")}</h2>
            <div className="flex flex-col space-y-4 max-h-[30vh] overflow-y-auto w-full">
              {serverMessages.map((msg) => (
                <div key={msg.id || msg.message_id || msg.message} className="bg-gray-100 p-4 rounded-lg shadow hover:shadow-md transition-shadow">
                  <div className="mb-3" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
                    {msg.title ? (
                      <span style={{ fontWeight: 700, fontSize: "15px", color: "#0F3F66" }}>{msg.title}</span>
                    ) : null}
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        height: "26px",
                        padding: "0 10px",
                        borderRadius: "8px",
                        background: "#D6E6F2",
                        color: "#0F3F66",
                        fontSize: "12.5px",
                        fontWeight: 700,
                        letterSpacing: "0.01em",
                      }}
                    >
                      {t("userui.menu.topicLabel", { topic: msg.topic_name || t("userui.menu.allTopics", "All topics") }, "Topic: {{topic}}")}
                    </span>
                  </div>
                  <p className="text-gray-800 mb-2" style={{ whiteSpace: "pre-wrap" }}>{msg.message}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setServerMessages([])}
                disabled={!okButtonEnabled}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                {okButtonEnabled ? t("common.actions.ok", "OK") : t("userui.menu.okCountdown", { seconds: secondsLeft }, "OK ({{seconds}}s)")}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Unsaved changes prompt */}
      {leavePrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-black">
            <h2 className="text-xl font-bold mb-3">{t("userui.menu.unsavedChanges", "Unsaved changes")}</h2>
            <p className="text-gray-700 mb-6">{t("userui.menu.unsavedPrompt", "You have unsaved changes. Do you want to save the document before leaving?")}</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={handleLeaveCancel}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
              >
                {t("common.actions.cancel", "Cancel")}
              </button>
              <button
                onClick={handleLeaveDiscard}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
              >
                {t("userui.menu.dontSave", "Don't save")}
              </button>
              <button
                onClick={handleLeaveSave}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                {t("common.actions.save", "Save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Menu;